// Shapes of the admin crash API (server/src/http/routes/admin-crash-routes.ts, admin-symbol-routes.ts).

export type CrashKind = 'crash' | 'hang'

export interface CrashFilters {
	days: number
	build: string
	kind: CrashKind | ''
	resolved: boolean
	shippingOnly: boolean
}

export interface Count {
	label: string
	count: number
}

export interface CrashGroup {
	signature: string
	title: string | null
	count: number
	installs: number
	firstReceived: string
	lastReceived: string
	builds: string[]
	kinds: string[]
	distributions: string[]
	latestReportId: string
}

export interface CrashOverview {
	stats: { last7Days: number; last30Days: number; installs: number; total: number; symbolicated: number }
	daily: { day: string; crashes: number; hangs: number }[]
	byBuild: Count[]
	byOs: Count[]
	groups: CrashGroup[]
	hasMore: boolean
}

export interface StackFrame {
	binaryName: string
	binaryUUID: string
	offset: string
	function: string | null
	file: string | null
	line: number | null
	inlines: { function: string; file: string | null; line: number | null }[]
}

export interface Stack {
	heading: string
	reportId: string
	frames: StackFrame[]
}

export interface CrashRow {
	id: string
	kind: CrashKind
	occurredAt: string
	receivedAt: string
	appBuild: string | null
	osVersion: string | null
	model: string | null
	symbolicationState: string
}

export interface CrashGroupDetail {
	signature: string
	title: string | null
	stack: Stack & { symbolicated: boolean }
	byModel: Count[]
	byOs: Count[]
	byBuild: Count[]
	reports: CrashRow[]
	hasMore: boolean
}

export interface CrashDetail {
	report: CrashRow & {
		title: string | null
		signature: string
		source: string
		bundleId: string
		appVersion: string | null
		platform: string | null
		distribution: string | null
		isSimulator: boolean
		installId: string
		accountId: string | null
		deviceId: string | null
		resolvedAt: string | null
		duplicateOf: string | null
		exceptionType: string | null
		exceptionCode: string | null
		signal: string | null
		terminationReason: string | null
		exceptionReason: string | null
	}
	copies: string[]
	stack: Stack
	payload: Record<string, unknown>
}

export interface SymbolFile {
	binaryUuid: string
	binaryName: string
	arch: string
	bundleId: string
	appVersion: string | null
	appBuild: string | null
	byteSize: number
	uploadedAt: string
	revision: string
}

export const DEFAULT_FILTERS: CrashFilters = { days: 30, build: '', kind: '', resolved: false, shippingOnly: false }

/** The filters as the API's query parameters. */
export function filterParams(f: CrashFilters) {
	return {
		days: f.days,
		build: f.build,
		kind: f.kind,
		resolved: f.resolved ? 'true' : undefined,
		shippingOnly: f.shippingOnly ? 'true' : undefined,
	}
}

/** The filters from a page's search params (what the groups list links carry). */
export function filtersFrom(params: URLSearchParams): CrashFilters {
	const kind = params.get('kind')
	return {
		days: Number(params.get('days')) || DEFAULT_FILTERS.days,
		build: params.get('build') ?? '',
		kind: kind === 'crash' || kind === 'hang' ? kind : '',
		resolved: params.get('resolved') === 'true',
		shippingOnly: params.get('shippingOnly') === 'true',
	}
}
